/**
 * «Νέα εμφάνιση» — γρήγορη προσθήκη (11/10/2026, απόφαση ιδιοκτήτη): μέγεθος ΚΑΙ υποχρεωτικές
 * επιλογές σε ΕΝΑ παράθυρο, αντί για δύο διαδοχικά.
 *
 * Προστίθεται μόλις το πάτημα συμπληρώσει ό,τι χρειάζεται — χωρίς «Προσθήκη»:
 *  - το μέγεθος (όταν είναι πάνω από ένα) θέλει ΡΗΤΟ πάτημα· το προεπιλεγμένο δεν μετρά, για να
 *    μη φύγει λάθος μέγεθος επειδή ο ταμίας πάτησε πρώτα τη ζάχαρη·
 *  - κάθε υποχρεωτική ομάδα μονής επιλογής θέλει μία επιλογή (η προεπιλογή της μετρά, όπως στο
 *    παλιό παράθυρο με «Προσθήκη»).
 * Με υποχρεωτική ομάδα πολλαπλής επιλογής το παράθυρο δεν ξέρει πότε τελείωσες: εκεί μένει το
 * «Προσθήκη». Τιμές και γραμμή καλαθιού: ό,τι και πριν (τα φτιάχνει ο γονέας στο onAdd).
 */
import React, { useState } from 'react';
import { useBackdropDismiss } from '../../utils/backdropDismiss';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import type { PosOptionGroup, Product, ProductSize, SelectedOptionEntry } from '../../types/order';
import { formatEuro } from '../../utils/formatEuro';

interface QuickAddSheetProps {
  product: Product;
  /** Τα μεγέθη που διαλέγει ο ταμίας — κενό/ένα = χωρίς επιλογή μεγέθους. */
  sizes: ProductSize[];
  /** Μόνο οι υποχρεωτικές ομάδες (όπως το γρήγορο πάτημα μέχρι σήμερα). */
  groups: PosOptionGroup[];
  /** Προεπιλεγμένες επιλογές (is_default) των ομάδων. */
  initialOptions: SelectedOptionEntry[];
  sizePrice: (size: ProductSize) => number;
  onAdd: (size: ProductSize | null, options: SelectedOptionEntry[]) => void;
  onClose: () => void;
}

const QuickAddSheet: React.FC<QuickAddSheetProps> = ({ product, sizes, groups, initialOptions, sizePrice, onAdd, onClose }) => {
  const { t } = useTranslation(['takeaway', 'common']);
  const needsSize = sizes.length > 1;
  const [size, setSize] = useState<ProductSize | null>(needsSize ? null : sizes[0] ?? null);
  const [options, setOptions] = useState<SelectedOptionEntry[]>(initialOptions);
  const hasMultiRequired = groups.some(g => g.selection_type === 'multiple');

  const complete = (nextSize: ProductSize | null, nextOptions: SelectedOptionEntry[]) =>
    (!needsSize || !!nextSize) && groups.every(g => nextOptions.some(o => o.group_id === g.id));

  const pickSize = (s: ProductSize) => {
    setSize(s);
    if (!hasMultiRequired && complete(s, options)) onAdd(s, options);
  };

  const pickChoice = (group: PosOptionGroup, choice: PosOptionGroup['choices'][number]) => {
    const entry: SelectedOptionEntry = {
      group_id: group.id, group_name: group.name, choice_id: choice.id, choice_name: choice.name, price_modifier: choice.price_modifier,
    };
    let next: SelectedOptionEntry[];
    if (group.selection_type === 'single') {
      next = [...options.filter(o => o.group_id !== group.id), entry];
    } else {
      const on = options.some(o => o.group_id === group.id && o.choice_id === choice.id);
      next = on ? options.filter(o => !(o.group_id === group.id && o.choice_id === choice.id)) : [...options, entry];
    }
    setOptions(next);
    if (group.selection_type === 'single' && !hasMultiRequired && complete(size, next)) onAdd(size, next);
  };

  // Το φόντο κλείνει μόνο με πάτημα που ξεκίνησε ΣΤΟ φόντο (το click της αφής, 16/10/2026).
  const backdrop = useBackdropDismiss(onClose);

  const choiceClass = (selected: boolean) =>
    `min-h-[64px] px-4 py-3 rounded-xl border text-center transition-colors active:scale-[0.98] touch-manipulation flex flex-col items-center justify-center ${
      selected ? 'bg-brand-soft border-brand text-brand' : 'bg-surface-2 border-line text-t1 hover:border-line-strong'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" {...backdrop} data-testid="quick-add-sheet">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-surface-2 border border-line shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
          <div className="min-w-0">
            {/* Χωρίς περιγραφή (απόφαση ιδιοκτήτη 15/10/2026: πουθενά στο ταμείο). */}
            <h2 className="text-xl font-bold text-t1 leading-tight break-words">{product.name}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
            className="w-11 h-11 shrink-0 rounded-xl hover:bg-surface-3 flex items-center justify-center text-t3">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 space-y-5">
          {needsSize && (
            <section>
              <h3 className="text-sm font-semibold text-t2 mb-2">{t('takeaway:v2.size', { defaultValue: 'Μέγεθος' })}</h3>
              <div className={`grid gap-2 ${sizes.length <= 2 ? 'grid-cols-2' : sizes.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                {sizes.map(s => (
                  <button key={s.id} type="button" aria-pressed={size?.id === s.id} onClick={() => pickSize(s)} className={choiceClass(size?.id === s.id)}>
                    <span className="font-semibold">{s.name}</span>
                    <span className="text-sm text-t3 tabular-nums mt-0.5">{formatEuro(sizePrice(s))}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {groups.map(group => (
            <section key={group.id}>
              <h3 className="text-sm font-semibold text-t2 mb-2">
                {group.name}
                {group.selection_type === 'multiple' && (
                  <span className="ml-2 font-normal text-t3">{t('takeaway:multiple', { defaultValue: 'πολλαπλή επιλογή' })}</span>
                )}
              </h3>
              <div className={`grid gap-2 ${group.choices.length <= 2 ? 'grid-cols-2' : group.choices.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                {group.choices.map(choice => {
                  const selected = options.some(o => o.group_id === group.id && o.choice_id === choice.id);
                  return (
                    <button key={choice.id} type="button" aria-pressed={selected} onClick={() => pickChoice(group, choice)} className={choiceClass(selected)}>
                      <span className="font-semibold">{choice.name}</span>
                      {choice.price_modifier > 0 && <span className="text-sm text-t3 tabular-nums mt-0.5">+{formatEuro(choice.price_modifier)}</span>}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* «Προσθήκη» μόνο όπου το παράθυρο δεν μπορεί να ξέρει ότι τελείωσες (πολλαπλή επιλογή) ή
            όταν όλα είναι ήδη συμπληρωμένα από προεπιλογές. */}
        {(hasMultiRequired || complete(size, options)) && (
          <div className="border-t border-line px-5 py-4">
            <button type="button" onClick={() => complete(size, options) && onAdd(size, options)} disabled={!complete(size, options)}
              className="w-full h-14 rounded-xl bg-brand text-brand-fg font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-40">
              <Check className="h-5 w-5" />
              {t('takeaway:addToCart', { defaultValue: 'Προσθήκη' })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickAddSheet;
